// Task 175 D7 — `<ScreenshotImage>` composite.
//
// Plain `<img>` tag — no provider SDK, no client-side imgproxy
// library, no URL construction. The component reads the signed URL
// via the `useScreenshotUrl` data-source hook (which is mockup-mode
// aware) and swaps it into the `src` once it resolves. While loading,
// the wrapper holds the requested dimensions so the page does not
// shift when the image arrives.
//
// `loading="lazy"` is the default per ADR 0038's "no client-side
// image library" decision. Callers that want eager loading can pass
// `loading="eager"`.
//
// Task 180 — `width` is optional. When the caller passes a number
// (the thumbnail-column case), behavior is unchanged: the wrapper
// reserves a fixed pixel box, the rendered `<img>` carries the
// `width`/`height` HTML attributes, and the signed-URL request
// includes `?width=…`. When the caller omits `width` (the lightbox
// case), the component drops the inline pixel sizing and switches to
// fluid CSS (`.fluid` on the img, `.fluidPlaceholder` on the loading
// box) so the browser scales the imgproxy-delivered native source
// down to the parent container's max-width/max-height. The signed
// URL has no `width` query param in that case, so imgproxy returns
// the largest variant it serves for the source.

import type { CSSProperties, ImgHTMLAttributes } from 'react';
import { useScreenshotUrl } from '@/hooks/useScreenshotUrl';
import styles from './ScreenshotImage.module.css';

interface ScreenshotImageProps extends Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  'src' | 'width' | 'height' | 'onClick'
> {
  screenshotId: string;
  /** Requested rendered width in CSS pixels. When omitted, the
   *  signed-URL request omits `?width=…` and imgproxy serves the
   *  source at its largest delivered variant; the rendered `<img>`
   *  is sized by CSS only (no `width`/`height` HTML attributes,
   *  no inline pixel style). The placeholder reserves a 16/9 fluid
   *  box during the loading phase. */
  width?: number;
  /** Requested rendered height. When omitted, imgproxy preserves aspect ratio. */
  height?: number;
  /** Optional output format override. Default lets imgproxy auto-pick. */
  format?: 'webp' | 'avif' | 'jpeg' | 'png';
  /** When provided, the visual layer (placeholder or img) is wrapped in
   *  a button so the user can open the screenshot in a lightbox. The
   *  button inherits the same dimensions so the column-width contract
   *  holds. When omitted, behavior is unchanged. */
  onClick?: () => void;
  /** aria-label for the click wrapper. Defaults to a generic prompt;
   *  callers should pass a row-specific phrase when they have one. */
  clickAriaLabel?: string;
}

export function ScreenshotImage({
  screenshotId,
  width,
  height,
  format,
  alt = '',
  className,
  loading = 'lazy',
  onClick,
  clickAriaLabel,
  ...rest
}: ScreenshotImageProps) {
  const { url, isLoading } = useScreenshotUrl({
    screenshotId,
    ...(width !== undefined ? { width } : {}),
    ...(height !== undefined ? { height } : {}),
    ...(format !== undefined ? { format } : {}),
  });

  const isFluid = width === undefined;

  // Reserve the requested dimensions on the wrapper so the page does
  // not shift between "resolving the URL" and "image bytes arrive".
  // Height is optional from the caller's POV; when omitted, the
  // wrapper falls back to the width as a defensive square so the
  // placeholder still has a known box. In the fluid (lightbox) case
  // we skip the inline pixel style entirely — the new
  // `.fluidPlaceholder` class reserves a 16/9 box via `aspect-ratio`.
  const wrapperStyle: CSSProperties | undefined = isFluid
    ? undefined
    : { width: `${width}px`, height: `${height ?? width}px` };

  const placeholderClass = isFluid ? styles.fluidPlaceholder : styles.placeholder;
  const imageClass = isFluid ? styles.fluid : styles.image;

  const visual =
    isLoading || !url ? (
      <div
        className={[styles.wrapper, placeholderClass, className].filter(Boolean).join(' ')}
        {...(wrapperStyle ? { style: wrapperStyle } : {})}
        data-testid="screenshot-placeholder"
        aria-busy={isLoading}
      />
    ) : (
      <img
        {...rest}
        src={url}
        {...(isFluid ? {} : { width, height })}
        alt={alt}
        loading={loading}
        className={[imageClass, className].filter(Boolean).join(' ')}
      />
    );

  if (!onClick) return visual;

  return (
    <button
      type="button"
      className={styles.clickWrapper}
      {...(wrapperStyle ? { style: wrapperStyle } : {})}
      onClick={onClick}
      aria-label={clickAriaLabel ?? 'Open screenshot in full size'}
    >
      {visual}
    </button>
  );
}
